import React, { useState } from 'react';
import {
  Grid,
  TextField,
  Box,
  Button,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useTranslation } from 'react-i18next';
import { ImageUploadService } from '../../../accounting/inventory/services/imageUploadService';

export type CustomTabSectionImagePosition = 'left' | 'right';
export type CustomTabSectionImageSize = 'small' | 'medium' | 'large' | 'xlarge';

export interface CustomTabSectionData {
  id: string;
  title: string;
  content: string;
  image?: string;
  imagePosition?: CustomTabSectionImagePosition;
  imageSize?: CustomTabSectionImageSize;
}

interface CustomTabSectionFieldsProps {
  section: CustomTabSectionData;
  onUpdate: (field: keyof CustomTabSectionData, value: string) => void;
  titlePlaceholder?: string;
  contentPlaceholder?: string;
}

export function CustomTabSectionFields({
  section,
  onUpdate,
  titlePlaceholder,
  contentPlaceholder,
}: CustomTabSectionFieldsProps) {
  const { t } = useTranslation('salesSystem');
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (file: File) => {
    try {
      setUploading(true);
      const [uploaded] = await ImageUploadService.uploadImages([file]);
      if (uploaded?.originalUrl) {
        onUpdate('image', uploaded.originalUrl);
      }
    } catch (error) {
      console.error('Section image upload failed:', error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {t('ecommerceAboutSettings.customTabs.sectionImage')}
        </Typography>
        {section.image ? (
          <Box
            component="img"
            src={section.image}
            alt={section.title || t('ecommerceAboutSettings.customTabs.sectionImage')}
            sx={{
              width: '100%',
              maxHeight: 180,
              objectFit: 'cover',
              borderRadius: 1,
              mb: 1,
              border: '1px solid #ddd',
            }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: 120,
              border: '2px dashed #ddd',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1,
              bgcolor: '#fafafa',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {t('ecommerceAboutSettings.customTabs.noImage')}
            </Typography>
          </Box>
        )}
        <TextField
          fullWidth
          size="small"
          label={t('ecommerceAboutSettings.customTabs.sectionImageUrl')}
          value={section.image || ''}
          onChange={(e) => onUpdate('image', e.target.value)}
          placeholder="https://..."
          sx={{ mb: 1 }}
        />
        <Button
          size="small"
          variant="outlined"
          component="label"
          startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
          disabled={uploading}
          sx={{ mb: section.image ? 1.5 : 0 }}
        >
          {uploading
            ? t('ecommerceCorporateHero.uploading')
            : t('ecommerceCorporateHero.uploadImage')}
          <input
            type="file"
            hidden
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                void handleUpload(file);
              }
              e.target.value = '';
            }}
          />
        </Button>
        {section.image && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('ecommerceAboutSettings.customTabs.sectionImagePosition')}</InputLabel>
              <Select
                label={t('ecommerceAboutSettings.customTabs.sectionImagePosition')}
                value={section.imagePosition || 'left'}
                onChange={(e) =>
                  onUpdate('imagePosition', e.target.value as CustomTabSectionImagePosition)
                }
              >
                <MenuItem value="left">{t('ecommerceCorporateHero.imageLeft')}</MenuItem>
                <MenuItem value="right">{t('ecommerceCorporateHero.imageRight')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>{t('ecommerceAboutSettings.customTabs.sectionImageSize')}</InputLabel>
              <Select
                label={t('ecommerceAboutSettings.customTabs.sectionImageSize')}
                value={section.imageSize || 'medium'}
                onChange={(e) =>
                  onUpdate('imageSize', e.target.value as CustomTabSectionImageSize)
                }
              >
                <MenuItem value="small">
                  {t('ecommerceAboutSettings.customTabs.sectionImageSizeSmall')}
                </MenuItem>
                <MenuItem value="medium">
                  {t('ecommerceAboutSettings.customTabs.sectionImageSizeMedium')}
                </MenuItem>
                <MenuItem value="large">
                  {t('ecommerceAboutSettings.customTabs.sectionImageSizeLarge')}
                </MenuItem>
                <MenuItem value="xlarge">
                  {t('ecommerceAboutSettings.customTabs.sectionImageSizeXlarge')}
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}
      </Grid>

      <Grid item xs={12} md={8}>
        <TextField
          fullWidth
          label={t('ecommerceAboutSettings.customTabs.sectionTitleOptional', {
            defaultValue: 'Bölüm Başlığı (Opsiyonel)',
          })}
          value={section.title}
          onChange={(e) => onUpdate('title', e.target.value)}
          variant="outlined"
          size="small"
          placeholder={titlePlaceholder}
          helperText={t('ecommerceAboutSettings.customTabs.sectionTitleOptionalHelper', {
            defaultValue: 'Boş bırakılabilir. Doluysa ana içerikte ve yan menüde görünür.',
          })}
        />
        <TextField
          fullWidth
          multiline
          rows={4}
          label={t('ecommerceAboutSettings.customTabs.sectionContent')}
          value={section.content}
          onChange={(e) => onUpdate('content', e.target.value)}
          variant="outlined"
          size="small"
          placeholder={contentPlaceholder}
          sx={{ mt: 2 }}
        />
      </Grid>
    </Grid>
  );
}
